import { ComponentType, createContext, Dispatch, ReactNode } from "react";

/**
 * Props passed to drawer components
 */
export interface DrawerProps {
    onClose: () => void;
}

/**
 * Configuration for a drawer
 */
export interface DrawerConfig {
    id: string;
    title: string;
    icon: ReactNode;
    component: ComponentType<DrawerProps>;
    /** When true, drawer sizes to its content instead of stretching to fill available height */
    fitContent?: boolean;
    /**
     * When true, the drawer's internal header (title, close button, drag handle) is not rendered.
     * Use for SDK-registered windows that supply their own header inside the component.
     */
    hideHeader?: boolean;
}

/**
 * Drawer state - only one drawer can be open at a time
 */
export interface State {
    /** ID of currently open drawer, or null if none */
    openDrawer: string | null;
    /** Registry of all available drawers */
    drawerRegistry: Record<string, DrawerConfig>;
}

export const initialState: State = {
    openDrawer: null,
    drawerRegistry: {},
};

enum ActionTypes {
    RegisterDrawer,
    UnregisterDrawer,
    OpenDrawer,
    CloseDrawer,
    CloseDrawerById,
    ToggleDrawer,
}

function registerDrawer(config: DrawerConfig) {
    return {
        type: ActionTypes.RegisterDrawer as const,
        config,
    };
}

function unregisterDrawer(drawerId: string) {
    return {
        type: ActionTypes.UnregisterDrawer as const,
        drawerId,
    };
}

function openDrawer(drawerId: string) {
    return {
        type: ActionTypes.OpenDrawer as const,
        drawerId,
    };
}

function closeDrawer() {
    return {
        type: ActionTypes.CloseDrawer as const,
    };
}

/**
 * Closes the drawer only if it matches the given ID.
 * Use this instead of {@link closeDrawer} when closing by a specific ID to avoid
 * accidentally closing a different drawer that happens to be open.
 */
function closeDrawerById(drawerId: string) {
    return {
        type: ActionTypes.CloseDrawerById as const,
        drawerId,
    };
}

function toggleDrawer(drawerId: string) {
    return {
        type: ActionTypes.ToggleDrawer as const,
        drawerId,
    };
}

export const actions = {
    registerDrawer,
    unregisterDrawer,
    openDrawer,
    closeDrawer,
    closeDrawerById,
    toggleDrawer,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchDrawers = Dispatch<Actions>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchDrawers>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.RegisterDrawer: {
            const drawerId = action.config.id;

            if (state.drawerRegistry[drawerId]) {
                console.warn(`Drawer "${drawerId}" is already registered`);
                return state;
            }

            return {
                ...state,
                drawerRegistry: {
                    ...state.drawerRegistry,
                    [drawerId]: action.config,
                },
            };
        }

        case ActionTypes.UnregisterDrawer: {
            const { [action.drawerId]: _removed, ...remainingDrawers } = state.drawerRegistry;

            return {
                ...state,
                drawerRegistry: remainingDrawers,
                // Also close the drawer if it was open
                openDrawer: state.openDrawer === action.drawerId ? null : state.openDrawer,
            };
        }

        case ActionTypes.OpenDrawer: {
            const { drawerId } = action;

            // Check if drawer is registered
            if (!state.drawerRegistry[drawerId]) {
                console.warn(`Drawer "${drawerId}" is not registered`);
                return state;
            }

            // Only one drawer at a time - opening a new one closes the current
            return {
                ...state,
                openDrawer: drawerId,
            };
        }

        case ActionTypes.CloseDrawer: {
            return {
                ...state,
                openDrawer: null,
            };
        }

        case ActionTypes.CloseDrawerById: {
            if (state.openDrawer !== action.drawerId) {
                return state;
            }
            return {
                ...state,
                openDrawer: null,
            };
        }

        case ActionTypes.ToggleDrawer: {
            const { drawerId } = action;

            // Check if drawer is registered
            if (!state.drawerRegistry[drawerId]) {
                console.warn(`Drawer "${drawerId}" is not registered`);
                return state;
            }

            if (state.openDrawer === drawerId) {
                // Close if same drawer
                return {
                    ...state,
                    openDrawer: null,
                };
            } else {
                // Open new drawer (closes any currently open)
                return {
                    ...state,
                    openDrawer: drawerId,
                };
            }
        }

        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
